import { Sparkles, X } from "lucide-react";

import { CampoDelCajon, TarjetaDelCajon } from "@/components/plantillas/PiezasDelCajon";
import { RotuloDeSeccion } from "@/components/plantillas/etiquetas";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import type { TipoDeContenido } from "@/lib/plantillas";
import {
  admiteRecurso,
  etiquetaDeLaSeccion,
  etiquetaDelModo,
  etiquetaDelRecurso,
  insigniaDeIa,
  MODOS_DE_PRODUCCION,
  modoEfectivo,
  NOTA_DEL_MODO,
  RECURSOS_DEL_DOCUMENTO,
  SECCIONES_DE_ORIGEN,
  type ModoDeProduccion,
  type RecursoDelDocumento,
} from "@/lib/produccionDelItem";
import { cn } from "@/lib/utils";

/**
 * La insignia "IA · final" / "IA · N" / "IA · ?" de las filas de un ítem
 * redactado por IA. Verde si ya se indicó de qué depende, ámbar si falta; con
 * trazo PUNTEADO cuando el modo es solo una sugerencia que nadie confirmó.
 * No se dibuja para los demás modos.
 */
export function InsigniaDeIa({
  modo,
  tipo,
  nombre,
  dependencias,
  alFinal,
  className,
}: {
  /** El modo confirmado; `null` si nadie lo confirmó (se usa la sugerencia). */
  modo: ModoDeProduccion | null;
  tipo: TipoDeContenido;
  nombre: string;
  dependencias: number;
  alFinal: boolean;
  className?: string;
}) {
  const efectivo = modoEfectivo(modo, tipo, nombre);
  const insignia = insigniaDeIa({ ...efectivo, dependencias, alFinal });
  if (!insignia) return null;

  return (
    <Badge
      variant="outline"
      title={insignia.titulo}
      className={cn(
        "text-[0.65625rem] font-bold",
        insignia.completa
          ? "border-success-dot/50 bg-success-soft text-success-soft-foreground"
          : "border-warning-dot/60 bg-warning-soft text-warning-soft-foreground",
        insignia.sugerida && "border-dashed",
        className,
      )}
    >
      <Sparkles aria-hidden="true" className="size-2.5" />
      {insignia.texto}
    </Badge>
  );
}

/** «Sugerido»: el modo que se muestra lo dedujo el sistema y nadie lo confirmó. */
function MarcaDeSugerido() {
  return (
    <span className="text-ink-muted inline-flex w-fit items-center rounded-full border border-dashed px-2 py-0.5 text-[0.65625rem] font-bold">
      Sugerido
    </span>
  );
}

interface EdicionDeProduccion {
  modo: ModoDeProduccion | null;
  seccionOrigen: string;
  seRedactaAlFinal: boolean;
  textoFijo: string;
  recurso: RecursoDelDocumento | "";
  dependencias: number[];
}

/**
 * «Generación con IA» del cajón del MAESTRO, donde SÍ se edita. Los controles
 * que dependen del modo solo existen con ese modo: la pestaña de origen con
 * «Texto ya validado», y las dependencias y «al final» con «Redactado por IA».
 *
 * El recurso («Qué muestra») aparece con los modos que lo admiten —sistema, tabla,
 * cálculo e imagen— y el texto fijo con «Texto fijo»; el servidor descarta el que
 * no corresponde al modo.
 *
 * Mientras no hay un modo confirmado el selector muestra la sugerencia, y
 * elegir la misma opción de un `<select>` no dispara cambio: por eso hay un
 * botón «Confirmar» que la guarda tal cual.
 */
export function SeccionGeneracionConIa({
  idDelItem,
  tipo,
  nombre,
  produccion,
  candidatos,
  onCambiar,
}: {
  /** `null` mientras el ítem solo existe en el navegador. */
  idDelItem: number | null;
  tipo: TipoDeContenido;
  nombre: string;
  produccion: EdicionDeProduccion;
  /** Los ítems compartidos del maestro: de ahí salen las dependencias posibles. */
  candidatos: { id: number; nombre: string }[];
  onCambiar: (cambios: Partial<EdicionDeProduccion>) => void;
}) {
  const { modo, sugerido } = modoEfectivo(produccion.modo, tipo, nombre);
  const nombreDe = (id: number) => candidatos.find((c) => c.id === id)?.nombre ?? `Ítem ${id}`;
  const posibles = candidatos.filter(
    (c) => c.id !== idDelItem && !produccion.dependencias.includes(c.id),
  );

  return (
    <TarjetaDelCajon>
      <RotuloDeSeccion>Generación con IA</RotuloDeSeccion>

      <CampoDelCajon id="maestro-modo" etiqueta="Cómo se produce">
        <NativeSelect
          id="maestro-modo"
          value={modo}
          onChange={(e) => onCambiar({ modo: e.target.value as ModoDeProduccion })}
        >
          {MODOS_DE_PRODUCCION.map((m) => (
            <option key={m.valor} value={m.valor}>
              {m.etiqueta}
            </option>
          ))}
        </NativeSelect>
      </CampoDelCajon>

      <p className="text-ink-muted text-xs leading-[1.45] font-medium text-pretty">
        {NOTA_DEL_MODO[modo]}
      </p>

      {sugerido ? (
        <div className="flex flex-col items-start gap-2 rounded-lg border border-dashed p-3">
          <MarcaDeSugerido />
          <p className="text-ink-muted text-xs leading-[1.45] font-medium text-pretty">
            Sugerido a partir del tipo y el nombre. Confírmalo o elige otro modo: el generador
            necesita uno confirmado.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onCambiar({ modo })}
          >
            Confirmar «{etiquetaDelModo(modo)}»
          </Button>
        </div>
      ) : null}

      {modo === "texto_validado" ? (
        <CampoDelCajon id="maestro-seccion-origen" etiqueta="Pestaña de origen">
          <NativeSelect
            id="maestro-seccion-origen"
            value={produccion.seccionOrigen}
            onChange={(e) => onCambiar({ seccionOrigen: e.target.value })}
          >
            <option value="">Elige una pestaña</option>
            {SECCIONES_DE_ORIGEN.map((s) => (
              <option key={s.valor} value={s.valor}>
                {s.etiqueta}
              </option>
            ))}
          </NativeSelect>
        </CampoDelCajon>
      ) : null}

      {admiteRecurso(modo) ? (
        <CampoDelCajon id="maestro-recurso" etiqueta="Qué muestra">
          <NativeSelect
            id="maestro-recurso"
            value={produccion.recurso}
            onChange={(e) => onCambiar({ recurso: e.target.value as RecursoDelDocumento | "" })}
          >
            <option value="">Sin definir</option>
            {RECURSOS_DEL_DOCUMENTO.map((r) => (
              <option key={r.valor} value={r.valor}>
                {r.etiqueta}
              </option>
            ))}
          </NativeSelect>
        </CampoDelCajon>
      ) : null}

      {modo === "texto_fijo" ? (
        <CampoDelCajon id="maestro-texto-fijo" etiqueta="Texto fijo">
          <Textarea
            id="maestro-texto-fijo"
            rows={6}
            value={produccion.textoFijo}
            onChange={(e) => onCambiar({ textoFijo: e.target.value })}
          />
          <p className="text-ink-muted text-xs leading-[1.45] font-medium text-pretty">
            Puedes usar marcadores del informe entre llaves dobles, como {"{{superficie_total}}"} o{" "}
            {"{{comunas}}"}; los que piden un sujeto se escriben {"{{superficie_plantada@cerezos}}"}.
            Si un dato no está, se imprime «—». Un marcador que no existe se rechaza al guardar.
          </p>
        </CampoDelCajon>
      ) : null}

      {modo === "redactado_ia" ? (
        <div className="flex flex-col gap-2">
          <span className="text-xs font-bold text-[#43454a] dark:text-foreground/80">
            Depende de
          </span>
          <div className="flex flex-wrap gap-1.5">
            {produccion.dependencias.map((id) => (
              <span
                key={id}
                className="bg-muted/60 text-ink inline-flex h-[1.875rem] items-center gap-1.5 rounded-[0.5625rem] pr-1.5 pl-2.5 text-xs font-bold"
              >
                {nombreDe(id)}
                <button
                  type="button"
                  aria-label={`Quitar dependencia ${nombreDe(id)}`}
                  title="Quitar"
                  onClick={() =>
                    onCambiar({ dependencias: produccion.dependencias.filter((d) => d !== id) })
                  }
                  className="text-ink-muted hover:bg-muted focus-visible:ring-ring/50 flex size-5 items-center justify-center rounded-md outline-none focus-visible:ring-[3px]"
                >
                  <X aria-hidden="true" className="size-2.5" />
                </button>
              </span>
            ))}
            {produccion.dependencias.length === 0 ? (
              <span className="text-ink-hint py-1.5 text-xs font-semibold">Sin dependencias</span>
            ) : null}
          </div>
          <NativeSelect
            aria-label="Agregar un ítem del que depende"
            value=""
            onChange={(e) => {
              const id = Number(e.target.value);
              if (id) onCambiar({ dependencias: [...produccion.dependencias, id] });
            }}
          >
            <option value="">Agregar un ítem del que depende…</option>
            {posibles.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
          </NativeSelect>
          <p className="text-ink-muted text-xs leading-[1.45] font-medium text-pretty">
            Si cambia alguno de estos ítems al generar el informe, esta sección queda marcada como
            desactualizada.
          </p>

          <div className="bg-superficie-sutil flex items-start gap-2.5 rounded-lg px-3 py-2.5">
            <Checkbox
              id="maestro-al-final"
              checked={produccion.seRedactaAlFinal}
              onCheckedChange={(marcado) => onCambiar({ seRedactaAlFinal: marcado === true })}
              className="mt-px"
            />
            <Label
              htmlFor="maestro-al-final"
              className="min-w-0 cursor-pointer flex-col items-start gap-0.5 leading-normal"
            >
              <span className="text-ink text-[0.78rem] font-bold">Se redacta al final</span>
              <span className="text-ink-muted text-xs font-medium text-pretty">
                Resume el resto del informe, como el resumen ejecutivo o las conclusiones. Depende
                de todas las demás secciones.
              </span>
            </Label>
          </div>
        </div>
      ) : null}
    </TarjetaDelCajon>
  );
}

/**
 * La misma sección, de SOLO LECTURA, para el cajón de una plantilla: estos
 * campos son del ítem y valen para todas las plantillas que lo usan, así que se
 * editan en el maestro (el cajón ya trae el enlace).
 */
export function ResumenDeGeneracionConIa({
  tipo,
  nombre,
  modo: confirmado,
  seccionOrigen,
  alFinal,
  dependencias,
  recurso,
  textoFijo,
}: {
  tipo: TipoDeContenido;
  nombre: string;
  modo: ModoDeProduccion | null;
  seccionOrigen: string;
  alFinal: boolean;
  dependencias: { id: number; nombre: string }[];
  recurso?: RecursoDelDocumento | null;
  textoFijo?: string;
}) {
  const { modo, sugerido } = modoEfectivo(confirmado, tipo, nombre);

  return (
    <section className="flex flex-col gap-2.5">
      <RotuloDeSeccion tono="hint">Generación con IA</RotuloDeSeccion>
      <div className="grid grid-cols-[8rem_minmax(0,1fr)] items-start gap-x-3 gap-y-2.5 text-[0.8125rem]">
        <span className="text-ink-hint font-semibold">Cómo se produce</span>
        <span className="text-ink-body flex min-w-0 flex-wrap items-center gap-2 font-semibold">
          <span>{etiquetaDelModo(modo)}</span>
          {sugerido ? <MarcaDeSugerido /> : null}
        </span>

        {modo === "texto_validado" ? (
          <>
            <span className="text-ink-hint font-semibold">Pestaña de origen</span>
            <span className="text-ink-body min-w-0 font-semibold">
              {seccionOrigen ? etiquetaDeLaSeccion(seccionOrigen) : "—"}
            </span>
          </>
        ) : null}

        {admiteRecurso(modo) ? (
          <>
            <span className="text-ink-hint font-semibold">Qué muestra</span>
            <span className="text-ink-body min-w-0 font-semibold">
              {recurso ? etiquetaDelRecurso(recurso) : "Sin definir"}
            </span>
          </>
        ) : null}

        {modo === "texto_fijo" ? (
          <>
            <span className="text-ink-hint font-semibold">Texto estándar</span>
            <span className="text-ink-body min-w-0 font-semibold break-words whitespace-pre-line">
              {textoFijo || "—"}
            </span>
          </>
        ) : null}

        {modo === "redactado_ia" ? (
          <>
            <span className="text-ink-hint font-semibold">Depende de</span>
            <span className="text-ink-body min-w-0 font-semibold">
              {alFinal
                ? "Todas las demás secciones (se redacta al final)"
                : dependencias.length > 0
                  ? dependencias.map((d) => d.nombre).join(", ")
                  : "Sin dependencias"}
            </span>
          </>
        ) : null}
      </div>
      <p className="text-ink-muted text-xs leading-[1.45] font-medium text-pretty">
        {NOTA_DEL_MODO[modo]}
      </p>
      {sugerido ? (
        <p className="text-ink-muted text-xs leading-[1.45] font-medium text-pretty">
          Sugerido a partir del tipo y el nombre. Se confirma en el maestro.
        </p>
      ) : null}
    </section>
  );
}
